import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../../../services/api';
import {
  ShoppingCart,
  DollarSign,
  Clock,
  CheckCircle,
  Search,
  RefreshCw,
  Download,
  XCircle,
  Calendar,
  CreditCard,
  User,
  Hash,
  MapPin,
  Ticket,
  ShoppingBag
} from 'lucide-react';
import './OrderManagementPanel.css';

const OrderManagementPanel = ({ mode = 'admin' }) => {
  const isPlannerMode = mode === 'planner';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: '30',
    search: ''
  });

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filters.status !== 'all') params.status = filters.status.toUpperCase();
      if (filters.dateRange !== 'all') params.days = filters.dateRange;
      if (filters.search.trim()) params.search = filters.search.trim();

      const fetchOrders = isPlannerMode ? adminAPI.getPlannerOrders : adminAPI.getOrders;
      const response = await fetchOrders(params);
      const payload = response?.data?.content || response?.content || response?.data || [];
      setOrders(Array.isArray(payload) ? payload : []);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError('Unable to load orders. Please try again later.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [filters, isPlannerMode]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const quickStats = useMemo(() => {
    if (!orders.length) {
      return [
        { label: 'Total Orders', value: 0, accent: '#5ac8ff', icon: ShoppingCart },
        { label: 'Pending', value: 0, accent: '#ffb347', icon: Clock },
        { label: 'Completed', value: 0, accent: '#34d399', icon: CheckCircle },
        { label: 'Total Revenue', value: '$0.00', accent: '#af52de', aux: 'Avg $0.00', icon: DollarSign }
      ];
    }

    const totalRevenue = orders.reduce((sum, order) => sum + Number(order.totalAmount || 0), 0);
    const completed = orders.filter((o) => (o.status || '').toUpperCase() === 'COMPLETED').length;
    const pending = orders.filter((o) => (o.status || '').toUpperCase() === 'PENDING').length;

    const formatShort = (n) => new Intl.NumberFormat('en-US', { notation: "compact" }).format(n);

    return [
      { label: 'Total Orders', value: formatShort(orders.length), accent: '#5ac8ff', icon: ShoppingCart },
      { label: 'Pending', value: formatShort(pending), accent: '#ffb347', icon: Clock },
      { label: 'Completed', value: formatShort(completed), accent: '#34d399', icon: CheckCircle },
      {
        label: 'Total Revenue',
        value: formatCurrency(totalRevenue),
        accent: '#af52de',
        aux: `Avg ${formatCurrency(totalRevenue / orders.length || 0)}`,
        icon: DollarSign
      }
    ];
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (!filters.search.trim()) return orders;
    const term = filters.search.trim().toLowerCase();

    return orders.filter((order) => {
      const candidates = [
        order.orderNumber,
        order.id,
        order.customerName,
        order.customerEmail,
        order.buyerName,
        order.buyerEmail,
        order.agentName,
        order.salesChannel,
        ...(order.orderItems || order.items || []).flatMap((item) => [
          item.ticketTypeName,
          item.ticketTypeCategory,
          item.eventName
        ])
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());

      return candidates.some((value) => value.includes(term));
    });
  }, [orders, filters.search]);

  function getStatusIcon(status) {
    const s = (status || '').toUpperCase();
    if (s === 'COMPLETED') return <CheckCircle size={12} />;
    if (s === 'PENDING') return <Clock size={12} />;
    if (s === 'CANCELLED') return <XCircle size={12} />;
    return null;
  }

  return (
    <div className="admin-orders-panel">
      <div className="orders-header">
        <div className="header-title-block">
          <h1><ShoppingCart size={24} className="text-blue-500" /> Order Management</h1>
          <p>{isPlannerMode ? 'Orders for your events' : 'Monitor live orders, revenue, and payment statuses'}</p>
        </div>

        <div className="header-controls">
          <button className="orders-control-btn secondary" onClick={() => loadOrders()} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Refreshing' : 'Refresh'}
          </button>
          <button className="orders-control-btn primary">
            <Download size={16} /> Export Orders
          </button>
        </div>
      </div>

      <div className="orders-stats-row">
        {quickStats.map((stat) => (
          <div className="orders-stat-card" key={stat.label} style={{ '--accent-color': stat.accent }}>
            <div className="stat-heading">
              <div className="stat-icon"><stat.icon size={20} /></div>
              <span className="stat-label">{stat.label}</span>
            </div>
            <div className="stat-value">{stat.value}</div>
            {stat.aux && <div className="stat-aux">{stat.aux}</div>}
          </div>
        ))}
      </div>

      <div className="orders-filter-bar">
        <div className="filter-group">
          <label>Status</label>
          <div style={{ position: 'relative' }}>
            <select
              className="filter-select"
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="filter-group">
          <label>Time Range</label>
          <div style={{ position: 'relative' }}>
            <select
              className="filter-select"
              value={filters.dateRange}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateRange: e.target.value }))}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>
        </div>

        <div className="search-wrapper">
          <Search size={16} className="search-icon-float" />
          <input
            type="text"
            className="filter-input search"
            placeholder="Search by ID, customer name/email..."
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          />
        </div>
      </div>

      {error && (
        <div className="orders-empty-state">
          <XCircle size={48} />
          <h3>Error loading orders</h3>
          <p>{error}</p>
        </div>
      )}

      {!error && (
        <div className="orders-table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Status</th>
                <th>Method</th>
                <th>Event</th>
                <th>Ticket Types</th>
                <th>Total</th>
                <th>Items</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="9">
                    <div className="orders-empty-state">
                      <ShoppingCart size={48} />
                      <h3>No orders found</h3>
                      <p>Try adjusting filters or search terms</p>
                    </div>
                  </td>
                </tr>
              )}
              {loading && filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="9">
                    <div className="orders-empty-state">
                      <h3>Loading orders...</h3>
                    </div>
                  </td>
                </tr>
              )}
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td className="order-id-cell">
                    <span className="mobile-label"><Hash size={13} /> ID</span>
                    #{order.orderNumber || order.id}
                  </td>
                  <td>
                    <span className="mobile-label"><User size={13} /> Customer</span>
                    <div className="customer-cell">
                      <span className="customer-name">{order.customerName || order.buyerName || 'Unknown'}</span>
                      <span className="customer-email">{order.customerEmail || order.buyerEmail || '—'}</span>
                    </div>
                  </td>
                  <td>
                    <span className="mobile-label"><Calendar size={13} /> Date</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={12} className="desktop-only-icon" style={{ opacity: 0.5 }} />
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '—'}
                    </span>
                  </td>
                  <td>
                    <div className="mobile-label-status">
                      <span className="mobile-label"><CheckCircle size={13} /> Status</span>
                    </div>
                    <span className={`status-pill ${(order.status || '').toLowerCase()}`}>
                      {getStatusIcon(order.status)}
                      {(order.status || 'unknown').toLowerCase()}
                    </span>
                  </td>
                  <td>
                    <span className="mobile-label"><CreditCard size={13} /> Method</span>
                    {(() => {
                      const channel = (order.salesChannel || order.source || '').toUpperCase();
                      if (channel === 'SALES_AGENT' || order.agentId) {
                        return <span title={order.agentName}><User size={12} style={{ marginRight: 4 }} /> Agent</span>;
                      }
                      return <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}><CreditCard size={12} /> Online</span>;
                    })()}
                  </td>
                  <td>
                    <span className="mobile-label"><MapPin size={13} /> Event</span>
                    {formatEventNames(order)}
                  </td>
                  <td>
                    <span className="mobile-label"><Ticket size={13} /> Tickets</span>
                    {formatTicketTypes(order)}
                  </td>
                  <td style={{ fontWeight: 600, color: '#10b981' }}>
                    <span className="mobile-label"><DollarSign size={13} /> Total</span>
                    {formatCurrency(order.totalAmount, order.currencyCode)}
                  </td>
                  <td>
                    <span className="mobile-label"><ShoppingBag size={13} /> Items</span>
                    {order.items?.length || order.orderItems?.length || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const formatCurrency = (amount, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);

const formatTicketTypes = (order) => {
  const items = order?.orderItems || order?.items || [];
  if (!items.length) return '—';
  const grouped = items.reduce((acc, item) => {
    const name = item.ticketTypeName || item.ticketTypeCategory || 'Ticket';
    const qty = item.quantity && item.quantity > 0 ? item.quantity : 1;
    acc[name] = (acc[name] || 0) + qty;
    return acc;
  }, {});

  return Object.entries(grouped)
    .map(([name, qty]) => `${name} x${qty}`)
    .join(', ');
};

const formatEventNames = (order) => {
  const items = order?.orderItems || order?.items || [];
  if (!items.length) return '—';
  const names = new Set();
  items.forEach((item) => {
    if (item.eventName) {
      names.add(item.eventName);
    }
  });
  return names.size ? Array.from(names).join(', ') : '—';
};

export default OrderManagementPanel;
